import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  MessageDetail,
  MessageRow,
  Overview,
  QueueActionResult,
  RetryPreview,
  SendNowPreview
} from "../types/messages.types.ts"

const BASE_URL = "/communication"

/** `queue_count`: messages not sent yet under the same filters, whichever view is asked for. */
export type MessageListPage = BasePagination<MessageRow> & {
  queue_count?: number
}

export const getMessages = (params: Record<string, string | number>) =>
  request.api.get<MessageListPage, BaseApiResponse<void>>(
    `${BASE_URL}/messages/`,
    params
  )

export const getMessage = (id: string) =>
  request.api.get<MessageDetail, BaseApiResponse<void>>(
    `${BASE_URL}/messages/${id}/`
  )

export const getOverview = (period: string) =>
  request.api.get<Overview, BaseApiResponse<void>>(`${BASE_URL}/overview/`, {
    period
  })

export interface MessageFilterOptions {
  senders: Array<{ id: string; name: string }>
  campaigns: Array<{
    id: string
    subject: string
    channel: "email" | "sms"
    created: string
  }>
}

export const getMessageFilterOptions = () =>
  request.api.get<MessageFilterOptions, BaseApiResponse<void>>(
    `${BASE_URL}/messages/filter-options/`
  )

export const answerReply = (replyId: string, body: string) =>
  request.api.post<BaseApiResponse<{ id: string }>, BaseApiResponse<void>>(
    `${BASE_URL}/replies/${replyId}/answer/`,
    { body }
  )

export const getSendNowPreview = (id: string) =>
  request.api.get<SendNowPreview, BaseApiResponse<void>>(
    `${BASE_URL}/messages/${id}/send-now/`
  )

/** `confirm_outside_hours` sets the company's quiet hours or send window aside for this message only. */
export const sendMessageNow = (
  id: string,
  body: { confirm_outside_hours: boolean }
) =>
  request.api.post<QueueActionResult, QueueActionResult>(
    `${BASE_URL}/messages/${id}/send-now/`,
    body
  )

export const getRetryPreview = (id: string) =>
  request.api.get<RetryPreview, BaseApiResponse<void>>(
    `${BASE_URL}/messages/${id}/retry/`
  )

/** `confirm_not_received`: for an uncertain failure, the sender checked the recipient never got it. */
export const retryMessage = (
  id: string,
  body: { confirm_not_received: boolean }
) =>
  request.api.post<QueueActionResult, QueueActionResult>(
    `${BASE_URL}/messages/${id}/retry/`,
    body
  )
